import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import LinkType from '../../../shared/enums/link-type.enums';
import { Sizes, Colors } from '../../../shared/enums/shared.enums';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { ModalType } from '../../../shared/enums/modal-type.enums';
import { Video } from '../../../shared/interfaces/video.interface';
import ViewportType from '../../../shared/enums/viewport-type.enums';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'hdi-link',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent],
  templateUrl: './link.component.html',
  styleUrl: './link.component.scss',
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class LinkComponent implements OnChanges {
  protected readonly LinkType = LinkType;

  @Input() href: string = ''; // Page URL
  @Input() fragment?: string = ''; // Section ID or fragment
  @Input() type!: LinkType;
  @Input() hrefType: HrefType = HrefType.ANGULAR_ROUTE;
  @Input() label: string = '';
  @Input() bold!: boolean;
  @Input() disabled: boolean = false;
  @Input() iconName?: string;
  @Input() iconPrefix?: string = 'far';
  @Input() target? = '_self';
  @Input() viewport?: ViewportType = ViewportType.desktop;
  @Input() color!: Colors;
  @Input() size?: Sizes = Sizes.INHERIT;
  @Input() video: Video | undefined;
  @Input() showModal = false;
  @Input() rel: string = 'follow';
  @Input() title: string = '';
  @Input() overLabel?: string;
  @Input() inModal: boolean = false;
  @Input() sizes?: Sizes;

  @Output() openFlyout = new EventEmitter<{ href: string; label: string }>();
  @Output() clickEvent = new EventEmitter();
  @Output() closeModalTrigger = new EventEmitter<boolean>();

  defaultIcon = 'gears';
  currentIcon = this.defaultIcon;

  ModalType = ModalType;
  HrefType = HrefType;
  Colors = Colors;

  videoId: string = '';
  Sizes = Sizes;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (!this.type) {
        this.setDefaultLinkType();
      }
      this.currentIcon = this.getTypeIcon();
    }
  }

  /**
   * Sets a default LinkType based on hrefType.
   */
  private setDefaultLinkType(): void {
    if (this.hrefType === HrefType.EXTERNAL) {
      this.type = LinkType.EXTERNAL;
    } else if (this.hrefType === HrefType.ANGULAR_ROUTE) {
      this.type = LinkType.PRIMARY_ARROW;
    } else if (this.hrefType === HrefType.OPEN_MODAL_VIDEO) {
      this.type = LinkType.VIDEO_LINK;
    } else if (this.hrefType === HrefType.OPEN_FLYOUT) {
      this.type = LinkType.SECONDARY_ICON;
    } else {
      this.type = LinkType.NOSTYLES;
    }
  }

  shouldDisplayRightIcon(): boolean {
    return (
      [
        LinkType.PRIMARY_ARROW,
        LinkType.PRIMARY_ICON,
        LinkType.EXTERNAL,
        LinkType.SECONDARY_EXTERNAL,
        LinkType.SECONDARY_EXTERNAL_NO_UNDERSCORE,
        LinkType.BREADCRUMB,
        LinkType.VIDEO_LINK,
        LinkType.VIDEO_LINK_NO_UNDERSCORE,
        LinkType.VIDEO_LINK_LIGHT,
        LinkType.MEDIA_LINK,
        LinkType.MEDIA_LINK_LIGHT,
        LinkType.FLYER_LINK,
        LinkType.DOWNLOAD,
        LinkType.DOWNLOAD_NO_UNDERSCORED,
        LinkType.SECONDARY_ICON,
        LinkType.SECONDARY_ICON_LIGHT,
      ].includes(this.type) ||
      (this.type === LinkType.NOSTYLES && !!this.iconName)
    );
  }
  isActiveLink(): boolean {
    const currentRoute = this.router.url.split('#')[0];
    const currentFragment = this.router.url.split('#')[1];
    return (
      this.href === currentRoute &&
      (!this.fragment || this.fragment === currentFragment)
    );
  }
  normalizeHref(href: string): string {
    const currentPath = this.router.url.split('#')[0];
    let baseHref = href.trim() === '/' || !href.trim() ? currentPath : href;

    baseHref = baseHref.startsWith('/') ? baseHref : `/${baseHref}`;

    return baseHref;
  }

  scrollToTop() {
    if (!this.disabled) {
      this.document.documentElement.scrollTo({ behavior: 'smooth', top: 0 });
    }
  }

  openModal(event: Event): void {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    if (this.video?.videoSrc) {
      // if (this.video?.videoSrc) {
      //   if (typeof window !== 'undefined') {
      //     const event = new CustomEvent('openVideoModal', { detail: this.video });
      //     window.dispatchEvent(event);
      //   }
      const modalEvent = new CustomEvent('openVideoModal', {
        detail: this.video,
      });
      window.dispatchEvent(modalEvent);
      this.showModal = true;
    } else {
      this.clickEvent.emit();
    }
  }
  getIconName(): string {
    if (this.iconName) {
      return this.iconName.trim();
    }
    return this.getTypeIcon() ?? this.defaultIcon;
  }

  getIconPrefix(): string {
    return this.iconPrefix || 'far';
  }

  private getTypeIcon(): string {
    switch (this.type) {
      case LinkType.PRIMARY_ARROW:
        return 'arrow-right-long';
      case LinkType.EXTERNAL:
        return 'arrow-up-right-from-square';
      case LinkType.BREADCRUMB:
        return 'arrow-right-long';
      case LinkType.DOWNLOAD_NO_UNDERSCORED:
        return 'arrow-down-to-line';
      case LinkType.DOWNLOAD:
        return 'arrow-down-to-line';
      case LinkType.VIDEO_LINK:
      case LinkType.VIDEO_LINK_NO_UNDERSCORE:
        return 'circle-play';
      case LinkType.VIDEO_LINK_LIGHT:
        return 'circle-play';
      case LinkType.MEDIA_LINK:
        return 'volume';
      case LinkType.MEDIA_LINK_LIGHT:
        return 'volume';
      default:
        return this.defaultIcon;
    }
  }
  isEmailLink(href: string): boolean {
    return href?.startsWith('mailto:');
  }
  isPhoneLink(href: string): boolean {
    return href?.startsWith('tel:');
  }
  isSpecialLink(href: string): boolean {
    return this.isEmailLink(href) || this.isPhoneLink(href);
  }
}
